import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { fmtINR, fmtINRFull, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Save, Send, Upload, Download, Trash2, RefreshCw, FileSpreadsheet } from "lucide-react";

const RA_TYPES = ["Material", "Labour", "Agency", "SOH", "P&M", "Escalation", "Reduction"];
const KPI_COLORS = {
  Material: "text-[#0D3B66]",
  Labour: "text-emerald-700",
  Agency: "text-amber-700",
  SOH: "text-violet-700",
  "P&M": "text-[#F97316]",
  Escalation: "text-rose-700",
  Reduction: "text-slate-600",
};

const compute = (l) => {
  const coeff = Number(l.co_eff) || 0;
  const rate = Number(l.resource_rate) || 0;
  const qty = Number(l.iow_qty) || 0;
  const costPerUnit = coeff * rate;
  const totalResource = coeff * qty;
  const resourceValue = totalResource * rate;
  return { costPerUnit, totalResource, resourceValue };
};

export default function RateAnalysisEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const [proj, setProj] = useState(null);
  const [doc, setDoc] = useState(null); // {status, lines, totals}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [openIow, setOpenIow] = useState({});
  const canEdit = ["budget_controller", "engineering_head", "admin"].includes(user?.role);

  const load = async () => {
    const [p, d] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/ra-editor`),
    ]);
    setProj(p.data);
    setDoc(d.data);
  };
  useEffect(() => { load(); }, [id]);

  const status = doc?.status || "Draft";
  const editable = canEdit && status === "Draft";

  // Group by IOW code preserving order
  const grouped = useMemo(() => {
    if (!doc?.lines) return [];
    const map = new Map();
    for (const l of doc.lines) {
      if (!map.has(l.iow_code)) {
        map.set(l.iow_code, {
          iow_code: l.iow_code,
          iow_desc: l.iow_desc,
          iow_unit: l.iow_unit,
          iow_qty: l.iow_qty,
          erp_code: l.erp_code,
          rows: [],
        });
      }
      map.get(l.iow_code).rows.push(l);
    }
    return Array.from(map.values());
  }, [doc]);

  // Live KPI totals from client state (so Co-Eff edits update instantly)
  const kpis = useMemo(() => {
    const per = {}; let total = 0;
    for (const l of doc?.lines || []) {
      const { resourceValue } = compute(l);
      per[l.type] = (per[l.type] || 0) + resourceValue;
      total += resourceValue;
    }
    return { per, total };
  }, [doc]);

  const updateCoeff = (globalIdx, val) => {
    if (!editable) return;
    const lines = [...(doc.lines || [])];
    lines[globalIdx] = { ...lines[globalIdx], co_eff: parseFloat(val) || 0 };
    setDoc({ ...doc, lines });
  };

  const removeLine = (globalIdx) => {
    if (!editable) return;
    const lines = [...(doc.lines || [])];
    lines.splice(globalIdx, 1);
    setDoc({ ...doc, lines });
  };

  const saveDraft = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post(`/projects/${id}/ra-editor`, { lines: doc.lines || [] });
      setDoc(data); setMsg("Draft saved.");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    setSaving(true); setMsg("");
    try {
      await api.post(`/projects/${id}/ra-editor`, { lines: doc.lines || [] });
      const { data } = await api.post(`/projects/${id}/ra-editor/submit`);
      setDoc(data); setMsg("Submitted.");
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true); setMsg("");
    try { const { data } = await api.post(`/projects/${id}/ra-editor/reset`); setDoc(data); setMsg("Reset to draft."); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onImport = async (lines) => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post(`/projects/${id}/ra-editor/import`, { lines });
      setDoc(data); setMsg(`Imported ${lines.length} resource lines from RA working.`);
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addRow = () => {
    if (!editable) return;
    const lines = [...(doc.lines || []), {
      erp_code: "", iow_code: "NEW", type: "Material", iow_unit: "",
      iow_desc: "", co_eff: 1, resource: "", resource_uom: "",
      resource_rate: 0, iow_qty: 0,
    }];
    setDoc({ ...doc, lines });
  };

  const exportXlsx = () => {
    const rows = [];
    rows.push([`${proj?.name || ""} · Rate Analysis · ${status}`]);
    rows.push([`Generated ${new Date().toLocaleString("en-IN")}`]);
    rows.push([]);
    rows.push([
      "ERP code", "IOW Code", "Type", "IOW Unit", "IOW",
      "Co-Eff", "Resources", "Resource UOM", "Resource Rate", "Cost / Unit",
      "IOW Qty", "Total Resource", "Resource Value",
    ]);
    for (const l of doc?.lines || []) {
      const c = compute(l);
      rows.push([
        l.erp_code || "", l.iow_code, l.type, l.iow_unit || "", l.iow_desc || "",
        Number(l.co_eff) || 0, l.resource || "", l.resource_uom || "",
        Number(l.resource_rate) || 0, c.costPerUnit,
        Number(l.iow_qty) || 0, c.totalResource, c.resourceValue,
      ]);
    }
    rows.push([]);
    rows.push(["", "", "", "", "TOTAL", "", "", "", "", "", "", "", kpis.total]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 42 },
      { wch: 10 }, { wch: 36 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 10 }, { wch: 14 }, { wch: 14 },
    ];
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RA working");
    const safe = (proj?.name || "project").replace(/[^A-Za-z0-9_-]+/g, "_");
    XLSX.writeFile(wb, `${safe}_RateAnalysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (!doc) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/projects/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0D3B66]" data-testid="rae-back">
          <ArrowLeft className="h-4 w-4" /> Back to project
        </Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Rate Analysis Editor</h1>
            <p className="mt-1.5 text-slate-500">{proj?.name} · <StatusChip status={status} /></p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="rae-export-btn"
              variant="outline"
              className="rounded-full"
              onClick={exportXlsx}
              disabled={(doc.lines || []).length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" /> Export Excel
            </Button>
            {editable && (
              <>
                <RAWorkingImport disabled={saving} onImport={onImport} setMsg={setMsg} />
                <Button data-testid="rae-add-btn" variant="outline" className="rounded-full" onClick={addRow}>
                  <Plus className="h-4 w-4 mr-1.5" /> Add line
                </Button>
                <Button data-testid="rae-save-btn" variant="outline" className="rounded-full" onClick={saveDraft} disabled={saving}>
                  <Save className="h-4 w-4 mr-1.5" /> Save draft
                </Button>
                <Button data-testid="rae-submit-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E]" onClick={submit} disabled={saving || (doc.lines || []).length === 0}>
                  <Send className="h-4 w-4 mr-1.5" /> Submit
                </Button>
              </>
            )}
            {status === "Submitted" && canEdit && (
              <Button data-testid="rae-reset-btn" variant="outline" className="rounded-full" onClick={reset} disabled={saving}>
                <RefreshCw className="h-4 w-4 mr-1.5" /> Reset to draft
              </Button>
            )}
          </div>
        </div>
        {msg && <div data-testid="rae-msg" className="mt-3 text-sm text-slate-600">{msg}</div>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {RA_TYPES.map((t) => (
          <KpiCard key={t} label={t} value={kpis.per[t] || 0} className={KPI_COLORS[t]} testId={`rae-kpi-${t.replace(/\W+/g, "-").toLowerCase()}`} />
        ))}
        <KpiCard label="Total" value={kpis.total} className="text-[#F97316]" testId="rae-kpi-total" strong />
      </div>

      {/* Grouped table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-xs" data-testid="rae-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <th className="px-2 py-2 text-left w-[72px]">ERP</th>
                <th className="px-2 py-2 text-left w-[86px]">Type</th>
                <th className="px-2 py-2 text-left">Resource</th>
                <th className="px-2 py-2 text-left w-[56px]">UOM</th>
                <th className="px-2 py-2 text-right w-[80px]">Res Rate</th>
                <th className="px-2 py-2 text-right w-[80px]">Co-Eff</th>
                <th className="px-2 py-2 text-right w-[92px]">Cost/Unit</th>
                <th className="px-2 py-2 text-right w-[76px]">IOW Qty</th>
                <th className="px-2 py-2 text-right w-[96px]">Total Res</th>
                <th className="px-2 py-2 text-right w-[104px]">Res Value</th>
                {editable && <th className="px-2 py-2 w-[32px]"></th>}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={editable ? 11 : 10} className="p-10 text-center text-slate-500" data-testid="rae-empty">
                  No rate analysis yet. Click &ldquo;Import Excel&rdquo; and upload the RA working sheet, or use &ldquo;Add line&rdquo;.
                </td></tr>
              )}
              {grouped.map((grp) => {
                const isOpen = openIow[grp.iow_code] !== false;
                return (
                  <RAGroup
                    key={grp.iow_code}
                    grp={grp}
                    isOpen={isOpen}
                    toggle={() => setOpenIow((s) => ({ ...s, [grp.iow_code]: !isOpen }))}
                    lines={doc.lines}
                    editable={editable}
                    updateCoeff={updateCoeff}
                    removeLine={removeLine}
                    colSpan={editable ? 11 : 10}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, className, testId, strong = false }) {
  return (
    <Card data-testid={testId} className={`p-3 border-slate-200 shadow-none ${strong ? "ring-1 ring-[#F97316]/30" : ""}`}>
      <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`mt-1 text-base font-bold tracking-tight ${className || "text-[#0D3B66]"}`}>{fmtINR(value)}</div>
    </Card>
  );
}

function RAGroup({ grp, isOpen, toggle, lines, editable, updateCoeff, removeLine, colSpan }) {
  const groupSum = grp.rows.reduce((a, r) => a + compute(r).resourceValue, 0);
  return (
    <>
      <tr className="bg-[#0D3B66]/5 border-b border-slate-200">
        <td colSpan={colSpan} className="px-3 py-2">
          <button
            data-testid={`rae-iow-toggle-${grp.iow_code}`}
            onClick={toggle}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-center gap-2 min-w-0">
              {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
              <span className="text-xs font-mono bg-white rounded px-1.5 py-0.5 text-slate-700 border border-slate-200 shrink-0">{grp.iow_code}</span>
              <span className="text-sm font-semibold text-[#0D3B66] truncate">{grp.iow_desc}</span>
              {grp.iow_unit && <span className="text-[11px] text-slate-500 shrink-0">· {grp.iow_unit}</span>}
              <span className="text-[11px] text-slate-500 shrink-0">· Qty {Number(grp.iow_qty || 0).toLocaleString("en-IN")}</span>
              {grp.erp_code && <span className="text-[10px] text-slate-400 shrink-0">· ERP {grp.erp_code}</span>}
            </div>
            <span data-testid={`rae-iow-sum-${grp.iow_code}`} className="text-sm font-semibold text-[#F97316] shrink-0">{fmtINR(groupSum)}</span>
          </button>
        </td>
      </tr>
      {isOpen && grp.rows.map((r) => {
        const globalIdx = lines.indexOf(r);
        const c = compute(r);
        return (
          <tr key={globalIdx} className="border-b border-slate-100 hover:bg-slate-50/40 text-xs" data-testid={`rae-row-${globalIdx}`}>
            <td className="px-2 py-1.5 text-slate-500 truncate">{r.erp_code || "—"}</td>
            <td className="px-2 py-1.5">
              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium">{r.type}</span>
            </td>
            <td className="px-2 py-1.5 text-slate-800 truncate max-w-[280px]" title={r.resource}>{r.resource}</td>
            <td className="px-2 py-1.5 text-slate-500 truncate">{r.resource_uom}</td>
            <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">{Number(r.resource_rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 4 })}</td>
            <td className="px-1.5 py-1">
              <Input
                data-testid={`rae-coeff-${globalIdx}`}
                type="number"
                step="0.0001"
                value={r.co_eff ?? 0}
                disabled={!editable}
                onChange={(e) => updateCoeff(globalIdx, e.target.value)}
                className="h-7 text-right text-xs px-1.5 tabular-nums disabled:opacity-70 disabled:cursor-default"
              />
            </td>
            <td className="px-2 py-1.5 text-right text-[#0D3B66] font-semibold tabular-nums" data-testid={`rae-cpu-${globalIdx}`}>
              {c.costPerUnit.toLocaleString("en-IN", { maximumFractionDigits: 4 })}
            </td>
            <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums">{Number(r.iow_qty || 0).toLocaleString("en-IN")}</td>
            <td className="px-2 py-1.5 text-right text-slate-700 tabular-nums" data-testid={`rae-totalres-${globalIdx}`}>
              {c.totalResource.toLocaleString("en-IN", { maximumFractionDigits: 4 })}
            </td>
            <td className="px-2 py-1.5 text-right text-emerald-700 font-semibold tabular-nums" data-testid={`rae-resval-${globalIdx}`}>
              {fmtINR(c.resourceValue)}
            </td>
            {editable && (
              <td className="px-1 py-1 text-right">
                <button
                  data-testid={`rae-remove-${globalIdx}`}
                  onClick={() => removeLine(globalIdx)}
                  className="text-slate-400 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
}

/**
 * Excel import for the "RA working" sheet only. Ignores Budget BOQ / SOR sheets.
 */
function RAWorkingImport({ onImport, disabled, setMsg }) {
  const inputRef = useRef(null);
  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      // Locate the RA working sheet specifically
      const raSheetName = wb.SheetNames.find((n) => /^ra[\s_]?working/i.test(n))
        || wb.SheetNames.find((n) => /ra.*working|rate.*analysis/i.test(n));
      if (!raSheetName) {
        setMsg("Could not find an 'RA working' sheet in the file.");
        return;
      }
      const ws = wb.Sheets[raSheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
      // Find header row: has "IOW Code" AND "Co-Eff" AND "Resource"
      let hIdx = -1;
      for (let i = 0; i < Math.min(rows.length, 40); i++) {
        const r = (rows[i] || []).map((c) => String(c || "").trim().toLowerCase());
        if (r.some((c) => c.includes("iow code")) && r.some((c) => c.includes("co-eff") || c.includes("coeff")) && r.some((c) => c.includes("resource"))) {
          hIdx = i; break;
        }
      }
      if (hIdx < 0) {
        setMsg("Header row not detected on 'RA working' sheet.");
        return;
      }
      const H = rows[hIdx].map((c) => String(c || "").trim().toLowerCase());
      const col = (patterns) => {
        for (const p of patterns) {
          const idx = H.findIndex((c) => p.test(c));
          if (idx !== -1) return idx;
        }
        return -1;
      };
      const erpCol = col([/erp.*code/, /^erp$/]);
      const iowCol = col([/iow.*code/]);
      const typCol = col([/^type$/]);
      const iowUnitCol = col([/iow.*unit/]);
      const iowDescCol = col([/^iow$/, /iow.*description/, /iow.*desc/]);
      const coeffCol = col([/co-?eff/, /coefficient/]);
      const resCol = col([/^resource$/, /^resources$/]);
      const resUomCol = col([/resource.*uom/, /res.*uom/]);
      const resRateCol = col([/resource.*rate/, /res.*rate/]);
      const iowQtyCol = col([/iow.*qty/]);
      if (iowCol < 0 || typCol < 0 || coeffCol < 0 || resCol < 0) {
        setMsg("Required columns (IOW Code, Type, Co-Eff, Resource) missing on 'RA working' sheet.");
        return;
      }
      const lines = [];
      let curr = null; // last seen IOW row info
      for (let i = hIdx + 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r) continue;
        const iow = r[iowCol];
        const typ = r[typCol];
        const res = r[resCol];
        if (iow != null && String(iow).trim()) {
          curr = {
            erp_code: erpCol >= 0 && r[erpCol] != null ? String(r[erpCol]).trim() : "",
            iow_code: String(iow).trim(),
            iow_unit: iowUnitCol >= 0 && r[iowUnitCol] != null ? String(r[iowUnitCol]).trim() : "",
            iow_desc: iowDescCol >= 0 && r[iowDescCol] != null ? String(r[iowDescCol]).trim() : "",
            iow_qty: iowQtyCol >= 0 ? (Number(r[iowQtyCol]) || 0) : 0,
          };
        }
        if (!curr) continue;
        if (typ == null || res == null) continue;
        const typStr = String(typ).trim();
        if (!typStr || typStr.toLowerCase() === "type") continue;
        if (!RA_TYPES.includes(typStr)) continue; // ignore unknown types
        lines.push({
          erp_code: curr.erp_code,
          iow_code: curr.iow_code,
          type: typStr,
          iow_unit: curr.iow_unit,
          iow_desc: curr.iow_desc,
          co_eff: Number(r[coeffCol]) || 0,
          resource: String(res).trim(),
          resource_uom: resUomCol >= 0 && r[resUomCol] != null ? String(r[resUomCol]).trim() : "",
          resource_rate: resRateCol >= 0 ? (Number(r[resRateCol]) || 0) : 0,
          iow_qty: curr.iow_qty,
        });
      }
      if (lines.length === 0) {
        setMsg("No valid resource lines found on 'RA working' sheet.");
        return;
      }
      await onImport(lines);
    } catch (er) {
      setMsg(String(er.message || er));
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handle}
        className="hidden"
        data-testid="rae-file-input"
      />
      <Button
        data-testid="rae-import-btn"
        onClick={pick}
        variant="outline"
        className="rounded-full"
        disabled={disabled}
      >
        <Upload className="h-4 w-4 mr-1.5" /> Import Excel
      </Button>
    </>
  );
}
